import api from "./api";
import type {
  PaginatedResponse,
  PaginationMeta,
} from "@/types/api.types";
import type {
  ConvertToContractPayload,
  ConvertToContractResult,
  Quotation,
  QuotationFormData,
  QuotationFilters,
  QuotationItem,
} from "@/types/quotation.types";

function mapListResponse<T>(r: {
  data: T[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}) {
  return {
    data: r.data.map((row) => normalizeQuotation(row as any)),
    meta: {
      current_page: r.page,
      per_page: r.per_page,
      total: r.total,
      last_page: r.total_pages,
    } as PaginationMeta,
  };
}

/** Legacy API labels from older backends / cached responses. */
const LEGACY_QUOTATION_STATUS: Record<string, Quotation["status"]> = {
  sent: "valid",
  approved: "valid",
  rejected: "lost",
};

const ALLOWED_STATUSES: Quotation["status"][] = [
  "draft",
  "valid",
  "expired",
  "lost",
  "cancelled",
  "partially_ordered",
  "ordered",
  "replied",
];

/** API returns snake_case detail; normalize for UI types. */
export function normalizeQuotation(raw: Record<string, unknown> | null | undefined): Quotation {
  const r = raw || {};
  const items = (r.items ?? r.lines ?? []) as QuotationItem[];
  const rawStatus = String(r.status ?? "draft");
  const mapped =
    LEGACY_QUOTATION_STATUS[rawStatus] ?? (rawStatus as Quotation["status"]);
  let status: Quotation["status"] = ALLOWED_STATUSES.includes(mapped)
    ? mapped
    : "draft";
  const docstatus = Number(r.docstatus ?? 0);
  if (docstatus === 0) {
    status = "draft";
  } else if (docstatus === 2 && status !== "lost") {
    status = "cancelled";
  }
  const validUntil = String(r.valid_until ?? "");
  if (
    docstatus === 1 &&
    status === "valid" &&
    validUntil &&
    !["ordered", "partially_ordered", "lost", "replied"].includes(status)
  ) {
    const until = new Date(validUntil);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    until.setHours(0, 0, 0, 0);
    if (!Number.isNaN(until.getTime()) && until < today) {
      status = "expired";
    }
  }
  return {
    id: String(r.id ?? ""),
    quotation_number: String(r.quotation_number ?? ""),
    project_id: (r.project_id as string) ?? null,
    project: r.project as Quotation["project"],
    client_id: (r.client_id as number | string) ?? undefined,
    customer_id: r.customer_id != null
      ? String(r.customer_id)
      : r.client_id != null
        ? String(r.client_id)
        : undefined,
    client: r.client as Quotation["client"],
    title: String(r.title ?? ""),
    status,
    items: Array.isArray(items)
      ? items.map((it: QuotationItem) => ({
          id: it.id,
          item_id: it.item_id ?? null,
          name: it.name ?? null,
          description: String(it.description ?? it.name ?? ""),
          quantity: Number(it.quantity ?? 0),
          unit_price: Number(it.unit_price ?? 0),
          total: Number(it.total ?? 0),
          unit: it.unit,
          configurables: Array.isArray(it.configurables)
            ? it.configurables.map((c: any) => ({
                id: c.id,
                configurable_id: c.configurable_id,
                configurable_label: c.configurable_label ?? null,
                configurable_field_type: c.configurable_field_type ?? null,
                value: c.value ?? null,
                price_modifier: Number(c.price_modifier ?? 0),
              }))
            : undefined,
        }))
      : [],
    subtotal: Number(r.subtotal ?? 0),
    vat_amount: Number(r.vat_amount ?? 0),
    total_amount: Number(r.total_amount ?? r.price ?? 0),
    discount: Number(r.discount ?? 0),
    valid_until: String(r.valid_until ?? ""),
    notes: String(r.notes ?? r.note ?? ""),
    terms_conditions: String(r.terms_conditions ?? ""),
    created_by: r.created_by as number | undefined,
    created_at: String(r.created_at ?? ""),
    updated_at: String(r.updated_at ?? r.created_at ?? ""),
    public_token: r.public_token as string | undefined,
    docstatus: Number(r.docstatus ?? 0) as Quotation['docstatus'],
    docstatus_label: (r.docstatus_label as Quotation['docstatus_label']) ?? 'draft',
    submitted_at: (r.submitted_at as string | null) ?? null,
    cancelled_at: (r.cancelled_at as string | null) ?? null,
    tax_template_id: (r.tax_template_id as string | null) ?? null,
    payment_term_id: (r.payment_term_id as string | null) ?? null,
    billing_location_id: (r.billing_location_id as number | null) ?? null,
    shipping_location_id: (r.shipping_location_id as number | null) ?? null,
    lost_reason_id: (r.lost_reason_id as number | null) ?? null,
    lost_reason_name: (r.lost_reason_name as string | null) ?? null,
    lost_reason_detail: (r.lost_reason_detail as string | null) ?? null,
    lost_competitors: Array.isArray(r.lost_competitors)
      ? (r.lost_competitors as Quotation["lost_competitors"])
      : [],
    amended_from: (r.amended_from as Quotation["amended_from"]) ?? null,
    amended_to: (r.amended_to as Quotation["amended_to"]) ?? null,
    first_replied_at: (r.first_replied_at as string | null) ?? null,
    public_view_count: Number(r.public_view_count ?? 0),
    issued_pdf: normalizeIssuedPdf(r.issued_pdf),
  };
}

function normalizeIssuedPdf(raw: unknown): Quotation["issued_pdf"] {
  if (!raw || typeof raw !== "object") return null;
  const pdf = raw as Record<string, unknown>;
  const id = pdf.id != null ? String(pdf.id) : "";
  if (!id) return null;
  return {
    id,
    filename: String(pdf.filename ?? ""),
    file_size: Number(pdf.file_size ?? 0),
    stored_at: String(pdf.stored_at ?? ""),
  };
}

/** Submitted + valid (or expired when allowed) — required to convert to contract. */
export function isQuotationConvertible(
  q: Pick<Quotation, "status" | "docstatus">,
  options?: { allowExpired?: boolean },
): boolean {
  if (Number(q.docstatus ?? 0) !== 1) return false;
  if (q.status === "valid") return true;
  if (q.status === "expired" && options?.allowExpired) return true;
  return false;
}

export function isQuotationOrdered(q: Pick<Quotation, "status">): boolean {
  return q.status === "ordered" || q.status === "partially_ordered";
}

function unwrap<T>(data: unknown): T {
  const d = data as Record<string, unknown> | undefined;
  if (d && typeof d === "object" && "data" in d && d.data !== undefined) {
    return d.data as T;
  }
  return data as T;
}

function formToCreatePayload(data: QuotationFormData, status = 1) {
  return {
    project_id: data.project_id || undefined,
    tax_template_id: data.tax_template_id || undefined,
    payment_term_id: data.payment_term_id || undefined,
    billing_location_id: data.billing_location_id ?? undefined,
    shipping_location_id: data.shipping_location_id ?? undefined,
    lines: data.items.map((it) => ({
      item_id: it.item_id ?? undefined,
      description: it.description,
      quantity: it.quantity,
      unit_price: it.unit_price,
      unit: it.unit ?? undefined,
      configurables: (it.configurables ?? []).map((cfg) => ({
        configurable_id: cfg.configurable_id,
        value: cfg.value,
      })),
    })),
    discount: data.discount ?? 0,
    valid_until: data.valid_until || undefined,
    title: data.title,
    note: data.notes ?? "",
    terms_conditions: data.terms_conditions ?? "",
    status,
  };
}

class QuotationService {
  async getQuotations(
    params?: QuotationFilters & { page?: number; page_size?: number },
  ): Promise<{ data: Quotation[]; meta: PaginationMeta }> {
    const response = await api.get<PaginatedResponse<Quotation>>(
      "/api/v1/quotations/",
      { params },
    );
    return mapListResponse(response.data as any);
  }

  async getQuotation(id: string): Promise<Quotation> {
    const response = await api.get(`/api/v1/quotations/${id}`);
    return normalizeQuotation(unwrap<Record<string, unknown>>(response.data));
  }

  async createQuotation(data: QuotationFormData): Promise<Quotation> {
    const response = await api.post(`/api/v1/quotations/`, formToCreatePayload(data));
    return normalizeQuotation(unwrap<Record<string, unknown>>(response.data));
  }

  async createForCustomer(
    customerId: string,
    crLicenseId: string | undefined,
    locationId: number | undefined,
    data: Partial<import("@/types/quotation.types").QuotationFormData> & { status?: number }
  ): Promise<Quotation> {
    const createPayload = {
      customer_id: customerId,
      cr_licence_id: crLicenseId || undefined,
      tax_template_id: data.tax_template_id || undefined,
      payment_term_id: data.payment_term_id || undefined,
      billing_location_id: data.billing_location_id ?? undefined,
      shipping_location_id: data.shipping_location_id ?? undefined,
      title: data.title || "",
      note: data.notes ?? "",
      terms_conditions: data.terms_conditions ?? "",
      discount: data.discount ?? 0,
      valid_until: data.valid_until
        ? ((data.valid_until as unknown) instanceof Date
            ? (data.valid_until as unknown as Date).toISOString().split("T")[0]
            : String(data.valid_until))
        : undefined,
      lines: (data.items ?? []).map((it) => ({
        item_id: it.item_id ?? undefined,
        description: it.description,
        quantity: it.quantity,
        unit_price: it.unit_price,
        unit: it.unit ?? undefined,
        configurables: (it.configurables ?? []).map((cfg) => ({
          configurable_id: cfg.configurable_id,
          value: cfg.value,
        })),
      })),
      status: data.status ?? 1,
    };
    const response = await api.post<any>("/api/v1/quotations/", createPayload);
    const raw = unwrap<Record<string, unknown>>(response.data);
    const q = normalizeQuotation(raw);
    if (q.id && locationId) {
      await api.post(`/api/v1/quotations/${q.id}/locations`, { location_id: locationId });
    }
    return q;
  }

  async getQuotationLocations(quotationId: string): Promise<any[]> {
    const response = await api.get<any>(`/api/v1/quotations/${quotationId}/locations`);
    return Array.isArray(response.data) ? response.data : response.data?.data ?? [];
  }

  async linkQuotationLocation(
    quotationId: string,
    locationId: number,
    crLicenseId?: string
  ): Promise<void> {
    await api.post(`/api/v1/quotations/${quotationId}/locations`, {
      location_id: locationId,
      cr_license_id: crLicenseId,
    });
  }

  async unlinkQuotationLocation(quotationId: string, locationId: number): Promise<void> {
    await api.delete(`/api/v1/quotations/${quotationId}/locations/${locationId}`);
  }

  async updateQuotation(
    id: string,
    data: Partial<QuotationFormData>,
  ): Promise<Quotation> {
    const payload: Record<string, unknown> = {};
    if (data.notes !== undefined) payload.note = data.notes;
    if (data.title !== undefined) payload.title = data.title;
    if (data.valid_until !== undefined) payload.valid_until = data.valid_until;
    if (data.terms_conditions !== undefined) payload.terms_conditions = data.terms_conditions;
    if (data.discount !== undefined) payload.discount = data.discount;
    if (data.tax_template_id !== undefined) payload.tax_template_id = data.tax_template_id;
    if (data.payment_term_id !== undefined) payload.payment_term_id = data.payment_term_id;
    if (data.billing_location_id !== undefined) payload.billing_location_id = data.billing_location_id;
    if (data.shipping_location_id !== undefined) payload.shipping_location_id = data.shipping_location_id;
    if (data.items !== undefined) {
      payload.lines = data.items.map((it) => ({
        // Send the stored line id so the API updates the row in place instead of
        // deleting and recreating it (which dropped the line's configurable choices).
        id: it.id ?? undefined,
        item_id: it.item_id ?? undefined,
        description: it.description,
        quantity: it.quantity,
        unit_price: it.unit_price,
        unit: it.unit ?? undefined,
        // Omitted (rather than sent as []) when the form has no choices for the line,
        // so an update cannot clear choices it never displayed.
        ...(it.configurables
          ? {
              configurables: it.configurables.map((cfg) => ({
                configurable_id: cfg.configurable_id,
                value: cfg.value,
              })),
            }
          : {}),
      }));
    }
    const response = await api.put(`/api/v1/quotations/${id}`, payload);
    return normalizeQuotation(unwrap<Record<string, unknown>>(response.data));
  }

  async deleteQuotation(id: string): Promise<void> {
    await api.delete(`/api/v1/quotations/${id}`);
  }

  async generatePDF(id: string): Promise<Blob> {
    const response = await api.get(`/api/v1/quotations/${id}/pdf`, {
      responseType: "blob",
    });
    return response.data;
  }

  async reprintPdf(id: string): Promise<Quotation> {
    const response = await api.post(`/api/v1/quotations/${id}/reprint`);
    return normalizeQuotation(unwrap<Record<string, unknown>>(response.data));
  }

  async sendEmail(id: string, email: string): Promise<void> {
    await api.post(`/api/v1/quotations/${id}/send-email`, { email });
  }

  async cloneQuotation(id: string): Promise<Quotation> {
    const response = await api.post(`/api/v1/quotations/${id}/clone`);
    return normalizeQuotation(unwrap<Record<string, unknown>>(response.data));
  }

  async convertToContract(
    id: string,
    payload?: ConvertToContractPayload,
  ): Promise<ConvertToContractResult> {
    const response = await api.post<any>(
      `/api/v1/quotations/${id}/convert-to-contract`,
      payload,
    );
    const raw = unwrap<any>(response.data);
    return raw;
  }

  async getQuotationsByProject(projectId: string): Promise<Quotation[]> {
    const response = await api.get<{ data: unknown[] }>(
      `/api/v1/projects/${projectId}/quotations`,
    );
    const rows = response.data?.data ?? [];
    return rows.map((r) => normalizeQuotation(r as Record<string, unknown>));
  }

  async updateStatus(id: string, status: string): Promise<Quotation> {
    if (status === "expired") {
      throw new Error("Expired is determined automatically from Valid Until date");
    }
    if (status === "lost") {
      throw new Error("Use setQuotationLost to mark a quotation as lost");
    }
    const STATUS_TO_INT: Record<string, number> = {
      draft: 1,
      valid: 2,
      sent: 2,
      approved: 3,
      rejected: 4,
    };
    const code = STATUS_TO_INT[status] ?? Number(status);
    const response = await api.patch(`/api/v1/quotations/${id}/status`, {
      status: code,
    });
    return normalizeQuotation(unwrap<Record<string, unknown>>(response.data));
  }

  async setQuotationLost(
    id: string,
    body: {
      lost_reason_id?: number | null;
      lost_reason_detail?: string;
      competitors?: { name: string; notes?: string }[];
    },
  ): Promise<Quotation> {
    const response = await api.post(`/api/v1/quotations/${id}/set-lost`, body);
    return normalizeQuotation(unwrap<Record<string, unknown>>(response.data));
  }

  async amendQuotation(id: string): Promise<Quotation> {
    const response = await api.post(`/api/v1/quotations/${id}/amend`);
    return normalizeQuotation(unwrap<Record<string, unknown>>(response.data));
  }

  async getOrderLostReasons(): Promise<{ id: number; name: string; is_active: boolean }[]> {
    const response = await api.get("/api/v1/order-lost-reasons");
    return Array.isArray(response.data) ? response.data : [];
  }

  async getPublicQuotation(token: string, hidePrices = false): Promise<Quotation> {
    const response = await api.get(`/api/v1/public/quotations/${token}`, {
      params: { hide_prices: hidePrices },
    });
    return normalizeQuotation(unwrap<Record<string, unknown>>(response.data));
  }

  async submitQuotation(id: string): Promise<Quotation> {
    const response = await api.post(`/api/v1/quotations/${id}/submit`);
    return normalizeQuotation(unwrap<Record<string, unknown>>(response.data));
  }

  async cancelQuotation(id: string): Promise<Quotation> {
    const response = await api.post(`/api/v1/quotations/${id}/cancel`);
    return normalizeQuotation(unwrap<Record<string, unknown>>(response.data));
  }
}

export default new QuotationService();
